<template>
  <div class="home">
    <HeroSection />


    <section class="agency-presentation">
      <h2 class="agency-presentation">Présentation de l'agence web et digitale</h2>
      <div class="agency-presentation__container">
        <div class="agency-presentation__description">
          <p>Chez Arynae, nous développons des sites web et des solutions digitales adaptées à vos besoins. Notre approche mêle créativité et expertise technique pour vous offrir une présence en ligne qui vous ressemble.</p>
          
          <h3>Notre cible</h3>
          <p>Nous accompagnons principalement les PME ambitieuses, les startups innovantes et les institutions en phase de transformation numérique qui cherchent à se démarquer dans leur secteur. Notre expertise est particulièrement valorisée par les entreprises qui souhaitent digitaliser leur offre sans disposer d'une équipe technique interne complète.</p>
          
          <h3>Les objectifs que nous vous aidons à atteindre</h3>
          <p>Nous vous accompagnons pour augmenter votre visibilité en ligne, convertir plus efficacement vos visiteurs en clients, automatiser vos processus métier et créer de nouvelles sources de revenus grâce au digital. Notre priorité est de maximiser le retour sur votre investissement.</p>
          
          <h3>Nos prestations</h3>
          <p>Notre équipe crée aussi bien des sites vitrines élégants que des applications SaaS complexes.</p>
          <p>Nos solutions No Code permettent aux projets à budget limité de démarrer rapidement avec des outils professionnels.</p>
          <p>Nous intégrons le référencement naturel (SEO) dès la conception pour garantir la visibilité de votre site.</p>
          <p>Nous créons des chatbots personnalisés qui améliorent l'expérience de vos visiteurs et facilitent les conversions.</p>
          
          <p>Avec Arynae, vous avez un partenaire qui comprend vos enjeux et transforme vos ambitions en solutions concrètes et efficaces.</p>
        </div>
        <div class="agency-presentation__image">
          <img
            src="/img/arynae-presentation.svg"
            alt="Arynae - Agence Web Digitale"
            class="agency-presentation__svg"
            width="400"
            height="400"
          />
        </div>
      </div>
    </section>

    <ServicesSection :with-cta="true" />
    
    <section class="about">
      <h2 class="about__title">Qui sommes-nous ?</h2>
      <div class="about__content">
        <div class="about__text">
          <p>Arynae est votre partenaire de confiance pour tous vos projets numériques et web. Nous allions expertise technique et créativité pour vous proposer des solutions digitales sur mesure.</p>
          <p>Notre équipe de professionnels passionnés vous accompagne à chaque étape de votre transformation digitale.</p>
          <div class="about__cta-container">
            <ButtonCTA to="/contact" class="about__cta">Nous contacter</ButtonCTA>
          </div>
        </div>
        <div class="about__image">
          <img 
            src="/img/arynae-stats.svg" 
            alt="Statistiques Arynae" 
            style="width: 100%; max-width: 450px; height: auto; margin-top: 70px;"
          />
        </div>
      </div>
    </section>
    
    <section class="testimonials">
      <h2 class="testimonials__title">Ce que disent nos clients</h2>
      <div class="testimonials__wrapper">
        <div class="testimonials__background">
          <div class="testimonials__circle testimonials__circle--left"></div>
          <div class="testimonials__circle testimonials__circle--right"></div>
          <div class="testimonials__circle testimonials__circle--bottom"></div>
        </div>

        <div class="testimonials__container">
          <!-- Premier témoignage -->
          <div class="testimonial">
            <div class="testimonial__content">
              <p>Transformation de notre site web réussie! Notre trafic a augmenté de +150% en seulement 3 mois avec une refonte SEO complète.</p>
            </div>
            <div class="testimonial__footer">
              <div class="testimonial__avatar" style="background-color: #7400b8"></div>
              <div class="testimonial__author">
                <strong>Marie D.</strong>
                <span>Dirigeante</span>
              </div>
            </div>
          </div>

          <!-- Deuxième témoignage -->
          <div class="testimonial">
            <div class="testimonial__content">
              <p>Lancement deux fois plus rapide avec le No Code. Nous avons conçu sur notre MVP en quelques jours et développé la version finale sans difficulté.</p>
            </div>
            <div class="testimonial__footer">
              <div class="testimonial__avatar" style="background-color: #ff006e"></div>
              <div class="testimonial__author">
                <strong>Pierre M.</strong>
                <span>Concepteur d'applications</span>
              </div>
            </div>
          </div>

          <!-- Troisième témoignage -->
          <div class="testimonial">
            <div class="testimonial__content">
              <p>Notre chatbot sur mesure traite 90% des demandes de notre service client, avec une hausse de 47% de la satisfaction client.</p>
            </div>
            <div class="testimonial__footer">
              <div class="testimonial__avatar" style="background-color: #5390d9"></div>
              <div class="testimonial__author">
                <strong>Sophie M.</strong>
                <span>Directrice marketing</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { useHead } from '@vueuse/head'
import HeroSection from '@/components/sections/HeroSection.vue'
import ServicesSection from '@/components/sections/ServicesSection.vue'
import ButtonCTA from '@/components/ui/ButtonCTA.vue'

export default {
  name: 'HomeView',
  components: {
    HeroSection,
    ServicesSection,
    ButtonCTA
  },
  setup() {
    useHead({
      title: 'Arynae | Agence digitale - Solutions No Code, SaaS, Chatbots & SEO',
      meta: [
        { name: 'description', content: 'Arynae est une agence digitale spécialisée en développement d\'applications SaaS, création de Chatbots, solutions No Code et en référencement SEO. Dynamisez votre présence en ligne avec nos services sur mesure.' },
        { name: 'keywords', content: 'agence digitale, référencement SEO, développement SaaS, création chatbot, solutions no code, transformation digitale, présence en ligne, Arynae' },
        { name: 'robots', content: 'index, follow' },
        { name: 'author', content: 'Arynae' },
        { 'http-equiv': 'Content-Type', content: 'text/html; charset=utf-8' },
        // Open Graph essentiels
        { property: 'og:type', content: 'website' },
        { property: 'og:title', content: 'Arynae | Agence digitale - Solutions No Code, SaaS, Chatbots & SEO' },
        { property: 'og:description', content: 'Arynae est une agence digitale spécialisée en développement d\'applications SaaS, création de Chatbots, solutions No Code et en référencement SEO. Dynamisez votre présence en ligne avec nos services sur mesure.' },
        { property: 'og:url', content: 'https://arynae.com' },
        { property: 'og:locale', content: 'fr_FR' },
        { property: 'og:image', content:'https://arynae.com/img/arynae-social-card.png' },
        { property: 'og:image:alt', content:'Agence digitale - Solutions No Code, SaaS, Chatbots & SEO' },
        // Twitter Card basiques
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: 'Arynae | Agence digitale - Solutions No Code, SaaS, Chatbots & SEO' },
        { name: 'twitter:description', content: 'Arynae est une agence digitale spécialisée en développement d\'applications SaaS, création de Chatbots, solutions No Code et en référencement SEO. Dynamisez votre présence en ligne avec nos services sur mesure.' },
        { name: 'twitter:image', content: 'https://arynae.com/img/arynae-social-card.png' },
        { name: 'viewport', content: 'width=device-width, initial-scale=1.0' }
      ],
      link: [
        { rel: 'canonical', href: 'https://arynae.com' },
        { rel: 'alternate', hreflang: 'fr', href: 'https://arynae.com' }
      ],
      htmlAttrs: {
        lang: 'fr'
      }
    })
    
    return {}
  },
  mounted() {
    // Appel au script de tracking
    fetch('/ip.php', { 
      method: 'GET',
      cache: 'no-store'
    }).catch(() => {
      // Ignorer silencieusement les erreurs de requête
    });
  }
}
</script>

<style lang="scss" scoped>
@import '@/assets/styles/_variables.scss';

.home {
  padding-bottom: $spacing-xl;
}

.about {
  padding: $spacing-xl 0;
  
  &__title {
    font-size: 24px;
    font-weight: bold;
    color: $dark-text;
    margin-bottom: $spacing-lg;
  }
  
  &__content {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;
    
    @media (min-width: $breakpoint-md) {
      flex-direction: row;
      align-items: center;
    }
  }
  
  &__text {
    flex: 1;
    
    p {
      margin-bottom: $spacing-md;
      line-height: 1.6;
    }
  }
  
  &__cta {
    margin-top: $spacing-sm;
  }
  
  &__image {
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  
  @media (max-width: 768px) {
    margin-top: -60px; // Réduit l'espace vertical sur mobile
    margin-bottom: -20px;
  }
}

&__image-placeholder {
  width: 100%;
  max-width: 400px;
  height: 300px;
  
  @media (max-width: 768px) {
    max-width: 100%; // Prend toute la largeur disponible
    height: auto; // Hauteur ajustée aux proportions
    min-height: 200px; // Hauteur minimale
  }
}
}

.testimonials {
  padding: $spacing-xl 0;
  position: relative;
  overflow: hidden;
  
  &__title {
    font-size: 24px;
    font-weight: bold;
    color: $dark-text;
    margin-bottom: $spacing-lg;
    text-align: center;
    position: relative;
    z-index: 1;
  }
  
  &__wrapper {
    position: relative;
    padding: $spacing-md 0;
    max-width: 1400px;
    margin: 0 auto;
  }
  
  &__container {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;
    position: relative;
    z-index: 1;
    
    @media (min-width: $breakpoint-md) {
      flex-direction: row;
      justify-content: center;
      gap: $spacing-lg;
    }
  }
  
  &__background {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 0;
  }
  
  &__circle {
    position: absolute;
    border-radius: 50%;
    
    &--left {
      width: 400px;
      height: 400px;
      left: -150px;
      top: 50%;
      transform: translateY(-50%);
      background-color: rgba(116, 0, 184, 0.1);
    }
    
    &--right {
      width: 400px;
      height: 400px;
      right: -150px;
      top: 50%;
      transform: translateY(-50%);
      background-color: rgba(255, 0, 110, 0.1);
    }
    
    &--bottom {
      width: 300px;
      height: 300px;
      bottom: -150px;
      left: 50%;
      transform: translateX(-50%);
      background-color: rgba(83, 144, 217, 0.1);
    }
  }
}

.testimonial {
  background-color: $white;
  border-radius: $border-radius-lg;
  padding: $spacing-md $spacing-lg;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.05);
  flex: 1;
  min-width: 0; /* Empêche le débordement des témoignages */
  max-width: 450px;
  
  @media (max-width: $breakpoint-md) {
    max-width: 100%;
  }
  
  &__content {
    text-align: center;
    padding: $spacing-sm 0 $spacing-md;
    
    p {
      font-size: 16px;
      font-weight: 500;
      color: $dark-text;
      line-height: 1.5;
      margin: 0;
      
      @media (max-width: $breakpoint-md) {
        font-size: 14px;
      }
    }
  }
  
  &__footer {
    display: flex;
    align-items: center;
    margin-top: $spacing-md;
  }
  
  &__avatar {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    margin-right: $spacing-sm;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
  }
  
  &__author {
    display: flex;
    flex-direction: column;
    
    strong {
      font-size: 16px;
      font-weight: 600;
      color: $dark-text;
      margin-bottom: 2px;
    }
    
    span {
      font-size: 14px;
      color: $medium-text;
    }
  }


  &__image-content {
  width: 100%;
  max-width: 200px; /* Réduire de 400px à 300px */
  height: 150px; /* Réduire de 300px à 250px */
  border-radius: $border-radius-lg;
  object-fit: contain;
}

&__cta-container {
  margin-top: $spacing-sm;
  margin-bottom: 20px; // Crée de l'espace sous le bouton
}

}

.agency-presentation {
  padding: $spacing-xl 0;
  background-color: #fbfcff;
  
  &__container {
    display: flex;
    flex-direction: column;
    gap: $spacing-lg;
    align-items: center;
    
    @media (min-width: $breakpoint-md) {
      flex-direction: row;
      align-items: center;
    }
  }
  
  &__text {
    flex: 1;
    
    p {
      margin-bottom: $spacing-md;
      line-height: 1.6;
    }
  }
  
  &__title {
    font-size: 36px;
    font-weight: bold;
    color: #4361ee;
    margin-bottom: $spacing-sm;
  }
  
  &__subtitle {
    font-size: 22px;
    color: $dark-text;
    margin-bottom: $spacing-md;
  }
  
  &__description {
    font-size: 16px;
    color: black;
    max-width: 600px;
    
    p {
      margin-bottom: 1rem;
    }
  }
  
  &__cta-container {
    margin-top: $spacing-md;
  }
  
  &__image {
    flex: 1;
    display: flex;
    justify-content: center;
    align-items: center;
  }
  
  &__svg {
    width: 100%;
    max-width: 400px;
    height: auto;
  }
}
</style>